import { Formik, Form as FormikForm } from 'formik';
import { Button, Form, Spinner } from 'react-bootstrap';
import PhoneInput from 'react-phone-number-input';
import AddressSection from './components/AddressSection';
import FaxSettingsForm from './components/FaxSettingsForm';
import { IClinic } from '@customTypes/clinician';
import { useState } from 'react';

interface ClinicFormProps {
  clinic: IClinic;
  onSave: (values: IClinic) => Promise<void>;
  onCancel: () => void;
}

export default function ClinicForm({ clinic, onSave, onCancel }: ClinicFormProps) {
  const [streetFocused, setStreetFocused] = useState(false);
  const [showAutoFaxModal, setShowAutoFaxModal] = useState(false);

  const initialValues: IClinic = {
    ...clinic,
    address: clinic.address || {
      street_address: '',
      city: '',
      region: '',
      postal_code: '',
      country: '',
    },
    phone: clinic.phone || '',
    fax: clinic.fax || '',
    fax_number_verified: clinic.fax_number_verified || false,
    is_fax_enabled: clinic.is_fax_enabled || false,
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await onSave(values);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, handleChange, setFieldValue, isSubmitting }) => (
        <FormikForm className='edit-form clinic-details-container'>
          <Form.Group className='mb-2' controlId='orgName'>
            <Form.Label>Organization Name</Form.Label>
            <Form.Control type='text' name='name' value={values.name} onChange={handleChange} />
          </Form.Group>

          <div className='row'>
            <Form.Group className='col-md-6 mb-2'>
              <Form.Label htmlFor='phone-input'>Phone Number</Form.Label>
              <PhoneInput
                id='phone-input'
                placeholder='Enter phone number'
                value={values.phone || undefined}
                onChange={(val?: string) => setFieldValue('phone', val || '')}
                defaultCountry='US'
                numberInputProps={{ className: 'form-control' }}
              />
            </Form.Group>

            <FaxSettingsForm
              fax={values.fax || ''}
              isFaxEnabled={values.is_fax_enabled || false}
              showAutoFaxModal={showAutoFaxModal}
              onFaxChange={(val?: string) => {
                setFieldValue('fax', val || '');
                if (!val) setFieldValue('is_fax_enabled', false);
              }}
              onAutoFaxChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.target.checked) {
                  setShowAutoFaxModal(true);
                } else {
                  setFieldValue('is_fax_enabled', false);
                }
              }}
              onConfirmAutoFax={() => {
                setFieldValue('is_fax_enabled', true);
                setShowAutoFaxModal(false);
              }}
              onHideModal={() => setShowAutoFaxModal(false)}
            />
          </div>

          <div className='mt-4 edit-form-extensions'>
            <AddressSection
              isEditing={true}
              address={values.address}
              streetFocused={streetFocused}
              onStreetFocus={() => setStreetFocused(true)}
              onStreetBlur={() => setTimeout(() => setStreetFocused(false), 200)}
              onChange={handleChange}
              onAddressSelect={(street, city, region, postal) => {
                setFieldValue('address.street_address', street);
                if (city) setFieldValue('address.city', city);
                if (region) setFieldValue('address.region', region);
                if (postal) setFieldValue('address.postal_code', postal);
              }}
            />
          </div>

          <div className='form-actions mt-4'>
            <Button variant='secondary' onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant='primary' type='submit' disabled={isSubmitting}>
              {isSubmitting ? (
                <Spinner as='span' animation='border' size='sm' role='status' aria-hidden='true' />
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </FormikForm>
      )}
    </Formik>
  );
}
